import { PenIcon } from '../icons/PenIcon';
import { PolaroidFrame } from '../PolaroidFrame';
import { CornerBadge } from './CornerBadge';
import { PortraitInner } from './PortraitInner';
import type { SuspectSlot } from './types';

export type SlotCardProps = {
  index: number;
  slot: SuspectSlot;
  /** Truthy iff the slot is editable; the polaroid + name become buttons. */
  onOpenEditor?: () => void;
};

/** Polaroid card for a single suspect — portrait, name, badges, ×. */
export const SlotCard = ({ index, slot, onOpenEditor }: SlotCardProps) => {
  const { id, name, mugshot, isHost, isSelf, onRemove } = slot;
  const showHostBadge = isHost === true;
  const showYouBadge = isSelf === true && !showHostBadge;

  return (
    <PolaroidFrame
      index={index}
      seed={id}
      className={
        isSelf
          ? 'outline-2 outline-offset-2 outline-[var(--color-pop)] [animation:self-glow_1.4s_ease-in-out_infinite]'
          : ''
      }
    >
      {showHostBadge && <CornerBadge>host</CornerBadge>}
      {showYouBadge && <CornerBadge>you</CornerBadge>}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="remove"
          className="absolute top-[-6px] right-[-6px] z-10 flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] border-ink bg-cream font-display font-extrabold text-[10px] text-ink shadow-[1px_1px_0_var(--color-ink)] hover:bg-und hover:text-white"
        >
          ×
        </button>
      )}

      <div className="flex w-full flex-col items-stretch gap-0.5">
        {onOpenEditor ? (
          <button
            type="button"
            onClick={onOpenEditor}
            className="relative flex aspect-[3/4] items-center justify-center overflow-hidden"
          >
            <PortraitInner mugshot={mugshot} index={index} />
          </button>
        ) : (
          <div className="relative flex aspect-[3/4] items-center justify-center overflow-hidden">
            <PortraitInner mugshot={mugshot} index={index} />
          </div>
        )}
        {onOpenEditor ? (
          <button
            type="button"
            onClick={onOpenEditor}
            aria-label="edit"
            className="flex w-full items-center justify-center gap-1 text-ink hover:text-und"
          >
            <span className="truncate font-display font-extrabold text-[13px] uppercase tracking-[0.04em]">
              {name}
            </span>
            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
              <PenIcon />
            </span>
          </button>
        ) : (
          <span className="block truncate text-center font-display font-extrabold text-[13px] text-ink uppercase tracking-[0.04em]">
            {name}
          </span>
        )}
      </div>
    </PolaroidFrame>
  );
};
